import { Injectable } from '@angular/core';
import { ApiService } from '../../General/Api/api.service';
import { catchError, defer, delay, firstValueFrom, lastValueFrom, of, retry, tap, throwError } from 'rxjs';
import {
  IAccesory,
  IFinishInspection,
  IInspectionInstruction,
  IInspectionOrder,
  IPhoto,
} from 'src/app/interfaces/Outputs';
import { InspectionOrderNotFound } from 'src/app/errors/custom';
import { ISummary } from 'src/app/interfaces/Outputs/Summary';
import { IFeature } from 'src/app/interfaces/Outputs/Feature';
import { Extension, Media } from 'src/app/interfaces/PWA/Files';
import { FileKeys } from 'src/app/interfaces/Outputs/FileKeys';
import { IBodyUploadFile } from 'src/app/interfaces/Inputs';
import { IPayloadToken } from 'src/app/interfaces/Global/PayloadToken';
import { jwtDecode } from 'jwt-decode';
import { ICarSelection } from 'src/app/interfaces/Outputs/CarSelection';
import { IDeclareformElements } from 'src/app/interfaces/Inputs/DeclareFormElement';
import { IDeclareEvaluation } from 'src/app/interfaces/Inputs/DeclareEvaluation';
import { IDynamicForm } from 'src/app/interfaces/Outputs/DynamicForm';
import { IPhotoAnalysisClientRequest } from 'src/app/interfaces/Inputs/PhotoAnalysis';
import {
  classifyStateResponse,
  PHOTO_STATE_ENDPOINT,
  StateFetchOutcome,
} from 'src/app/utils/StateReconciliation';
import {
  FINALIZE_CLIENT_TIMEOUT_MS,
  FINALIZE_ENDPOINT,
  shouldRetryFinalize,
} from 'src/app/utils/FinalizeClose';
import {
  classifyEvaluationResponse,
  EVALUATION_ENDPOINT,
  EvaluationFetchOutcome,
} from 'src/app/utils/EvaluationScreen';
import {
  classifyStorageCheck,
  mergeStorageChecks,
  splitStorageCheckNames,
  STORAGE_CHECK_ENDPOINT,
  StorageCheckOutcome,
} from 'src/app/utils/StorageCheck';

@Injectable({
  providedIn: 'root',
})
export class InspectionApiService {
  constructor(private readonly apiService: ApiService) { }

  public getInspectionData(): IPayloadToken {
    const jwt = localStorage.getItem('jwt');
    if (!jwt) {
      throw new Error('No JWT found');
    }
    return jwtDecode(jwt) as IPayloadToken;
  }

  private async get(endpoint: string): Promise<any> {
    return await lastValueFrom(await this.apiService.get(endpoint));
  }

  private async post(endpoint: string, body: any): Promise<any> {
    return await lastValueFrom(await this.apiService.post(endpoint, body));
  }

  public async checkInspectionOrder(
    oiEncriptada: string
  ): Promise<IInspectionOrder> {
    try {
      const res = (
        await this.post('inspection/check/order', {
          oi_encriptada: oiEncriptada,
          firebase_token: 'jasdfklajskmrhzkqmrsh', // TODO! Esto esta hardocodeado?
        })
      ).InspectionOrder as IInspectionOrder;
      return res;
    } catch (error) {
      throw new InspectionOrderNotFound('No existen inspecciones pendientes');
    }
  }

  // TODO: Tal vez pantalla de instrucciones y summary se podrían unificar
  public async getInspectionInstructions(
    oiEncriptada: string
  ): Promise<IInspectionInstruction[]> {
    const instructions = (
      await this.get(`resources/instruction?oi_encriptada=${oiEncriptada}`)
    ).InstructionScreen as IInspectionInstruction[];
    return instructions;
  }

  public async startInspectionOrder(orderData: IInspectionOrder): Promise<{ jwt: string }> {
    try {
      // const orderData = await this.checkInspectionOrder();
      const inspectionTime = parseInt(orderData.timer_inspection as unknown as string);
      const startInspection = await lastValueFrom(
        await this.apiService.post('inspection/start', {
          inspection_id: parseInt(orderData.id as unknown as string),
          inspection_time: inspectionTime,
          // inspection_time: 60000,
          email: orderData.email,
          inspection_type: orderData.type,
          whatsapp_number: orderData.whatsapp_number,
          whatsapp_text: orderData.whatsapp_text,
          phone_number: orderData.phone_number,
          business_hours: orderData.business_hours,
          business_week: orderData.business_week,
          rut: orderData.rut,
          car_plate: orderData.patente,
          name: orderData.name,
          last_name: orderData.lastname,
          url_logo: orderData.img_logo,
          id_ramo: orderData.id_ramo,
        })
      );
      return {
        jwt: startInspection.StartInspection
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async getSummaryScreen(oiEncriptada: string): Promise<ISummary> {
    const summary = (
      await this.get(`resources/summary?oi_encriptada=${oiEncriptada}`)
    ).SummaryScreen as ISummary;
    return summary;
  }

  public async getCarSelectionScreen(): Promise<ICarSelection> {
    const carSelection = (await this.get(`resources/car-selection`))
      .CarSelectionScreen as ICarSelection;
    return carSelection;
  }

  public async getFeatureScreen(): Promise<IFeature> {
    const feature = (await this.get(`resources/feature`))
      .FeatureScreen as IFeature;
    return feature;
  }

  public async declareFeature(
    field_id: number,
    value: string
  ): Promise<void> {
    await this.post('inspection/declare/feature', {
      field_id,
      value,
    })
  }

  public async getAccesoryScreen(): Promise<IAccesory> {
    const accesory = (await this.get(`resources/accesory`))
      .AccesoryScreen as IAccesory;
    return accesory;
  }

  public async declareAccesory(
    field_id: number,
    value: string
  ): Promise<void> {
    // TODO: Quizás cambiar en el back a que todos los valores se puedan recibir como string,
    // TODO: Para permitir formularios dinámicos
    await this.post('inspection/declare/accesory', {
      field_id,
      value: value === 'true',
    });
  }

  public async getDynamicForm(
    section_id: number
  ): Promise<IDynamicForm> {
    const form = (await this.get(`resources/dynamic?section_id=${section_id}`)) as IDynamicForm;
    return form;
  }

  public async declareFormElements(
    input: IDeclareformElements
  ): Promise<void> {
    await this.post('inspection/declare/form', input);
  }

  /** Los veredictos persistidos de las fotos de la OI. Nunca lanza: un fallo vuelve clasificado. */
  public async getPhotoAnalysisState(timeoutMs: number): Promise<StateFetchOutcome> {
    const { status, body } = await this.apiService.getWithStatus(PHOTO_STATE_ENDPOINT, timeoutMs);
    return classifyStateResponse(status, body);
  }

  public async getPhotoScreen(): Promise<IPhoto> {
    const response = await this.get(`resources/photo`)
    const photo = response.PhotoScreen as IPhoto;
    return photo;
  }

  public async getFileDeclareKeysAndPresignedUrl(ext: Extension, photo_id?: number): Promise<FileKeys> {
    const keys = await this.post('storage/signed', {
      ext: ext,
      photo_id,
    }) as FileKeys;
    return keys;
  }

  /**
   * Sube el archivo con la modalidad que el servidor firmó, no con una elegida acá.
   *
   * El Storage Manager manda `post` o `signedUrl`, nunca las dos, y hoy manda `signedUrl` al 100 % de
   * los clientes porque POST está apagado hasta que exista `POST_MIN_APP_VERSION`. Elegir por lo
   * recibido es lo que permite que la modalidad se encienda del lado servidor sin tocar la app.
   */
  public async uploadPhoto(
    keys: FileKeys,
    data: Media,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    if (keys.post) {
      await lastValueFrom(
        await this.apiService.postExternal(
          keys.post.url, keys.post.fields, data, 3, 1000, onProgress
        )
      );
      return;
    }
    if (keys.signedUrl) {
      await lastValueFrom(
        await this.apiService.putExternal(keys.signedUrl, data, 3, 1000, onProgress)
      );
      return;
    }
    // Ni una ni otra: es un fallo del servidor, no del archivo. Se lanza como error común para que
    // la cola lo reintente — una firma ausente puede ser transitoria y el archivo está intacto.
    throw new Error(
      'El servicio de almacenamiento no entregó una URL de subida (ni POST ni PUT).'
    );
  }

  public async declarePhoto(props: IBodyUploadFile): Promise<void> {
    const res = await this.post('inspection/declare/file', props);
  }

  public async declareLogSkipPhoto(photo_required_id: number): Promise<void> {
    await this.post('inspection/declare/skip-photo', {
      photo_required_id,
    });
  }

  public async getFinishScreen(): Promise<IFinishInspection> {
    const finish = (await this.get(`resources/finish`))
      .FinishScreen as IFinishInspection;
    return finish;
  }

  /** Lanza si el cierre no se confirmó. Cuándo se reintenta solo está en `utils/FinalizeClose`. */
  public async declareFinalizeInspection(email: string): Promise<void> {
    await lastValueFrom(
      await this.apiService.post(FINALIZE_ENDPOINT, { email }, 3, 1000, {
        timeoutMs: FINALIZE_CLIENT_TIMEOUT_MS,
        retryWhen: shouldRetryFinalize,
      })
    );
  }

  /**
   * Qué archivos existen en S3, por nombre. Nunca lanza: un fallo vuelve clasificado como no disponible.
   * Las partes van en paralelo, para que repartir el pedido no sume plazos.
   */
  public async checkStorageFiles(names: string[], timeoutMs: number): Promise<StorageCheckOutcome> {
    const outcomes = await Promise.all(splitStorageCheckNames(names).map(async (part) => {
      const { status, body } = await this.apiService.postWithStatus(STORAGE_CHECK_ENDPOINT, { names: part }, timeoutMs);
      return classifyStorageCheck(status, body, part);
    }));
    return mergeStorageChecks(outcomes);
  }

  /** Nunca lanza ni pasa por la pantalla de error: un fallo vuelve clasificado. */
  public async getEvaluationScreen(timeoutMs: number): Promise<EvaluationFetchOutcome> {
    const { status, body } = await this.apiService.getWithStatus(EVALUATION_ENDPOINT, timeoutMs);
    return classifyEvaluationResponse(status, body);
  }
  
  public async declareEvaluation(input: IDeclareEvaluation): Promise<void> {
    await this.post('inspection/declare/evaluation', input);
  }

  public async analyzePhoto(input: IPhotoAnalysisClientRequest): Promise<void> {
    await this.post('photo-analysis/analyze', input);
  }

}
